import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, ShoppingBag, Tag, ArrowLeft, Eye, EyeOff, User } from 'lucide-react';
import { PlexataIcon } from '../components/PlexataLogo';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

const COLOR_MAP = {
  Gold: { bg: 'bg-gradient-to-br from-amber-300 to-yellow-500', ring: 'ring-amber-400', label: 'text-amber-700', image: '/ring-gold.png' },
  Silver: { bg: 'bg-gradient-to-br from-gray-300 to-slate-400', ring: 'ring-slate-400', label: 'text-slate-600', image: '/ring-silver.png' },
  Black: { bg: 'bg-gradient-to-br from-gray-800 to-gray-900', ring: 'ring-gray-700', label: 'text-gray-700', image: '/ring-black.png' },
};

const Store = () => {
  const { user, login, initiateGoogleAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [promo, setPromo] = useState(null);
  const [color, setColor] = useState('Black');
  const [size, setSize] = useState(10);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Account creation fields for new users
  const [accountForm, setAccountForm] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
  });

  useEffect(() => {
    axios.get(`${API}/store/products`).then(r => {
      setProduct(r.data.product);
      setPromo(r.data.promo);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderResult = searchParams.get('order');
    if (sessionId && orderResult === 'success') {
      axios.get(`${API}/store/order/${sessionId}`).then(r => setOrderStatus(r.data)).catch(() => setOrderStatus({ status: 'paid' }));
    }
  }, [searchParams]);

  const applyPromo = async () => {
    try {
      const res = await axios.post(`${API}/store/validate-promo`, { code: promoCode });
      if (res.data.valid) {
        setPromoApplied(res.data);
      } else {
        setPromoApplied({ valid: false });
      }
    } catch { setPromoApplied({ valid: false }); }
  };

  const validateForm = () => {
    if (!user) {
      if (!accountForm.name.trim()) return 'Name is required';
      if (!accountForm.email.trim()) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountForm.email)) return 'Invalid email format';
      if (!accountForm.username.trim()) return 'Username is required';
      if (accountForm.username.length < 3) return 'Username must be at least 3 characters';
      if (!/^[a-zA-Z0-9_]+$/.test(accountForm.username)) return 'Username can only contain letters, numbers, and underscores';
      if (!accountForm.password) return 'Password is required';
      if (accountForm.password.length < 6) return 'Password must be at least 6 characters';
    }
    return null;
  };

  const checkout = async () => {
    setError('');
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const checkoutData = {
        color,
        size,
        promo_code: promoApplied?.valid ? promoCode : null,
        origin_url: SITE_URL,
      };

      // If user is logged in, use their existing account
      if (user) {
        checkoutData.user_id = user.id;
      } else {
        // Include account creation data for new users
        checkoutData.new_account = {
          name: accountForm.name.trim(),
          email: accountForm.email.trim().toLowerCase(),
          username: accountForm.username.trim().toLowerCase(),
          password: accountForm.password,
        };
      }

      const res = await axios.post(`${API}/store/checkout`, checkoutData);
      
      // If a new account was created, log them in
      if (res.data.token) {
        login(res.data.token, res.data.user);
      }
      
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally { setLoading(false); }
  };

  const finalPrice = product ? product.price - (promoApplied?.valid ? promoApplied.discount : 0) : 0;

  // Order success screen
  if (orderStatus) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="order-success">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-heading text-3xl font-normal text-gray-900 mb-3">Order Confirmed!</h1>
          <p className="text-sm text-gray-500 mb-2">
            Your {orderStatus.color || ''} Plexata Ring (Size {orderStatus.size || ''}) is being prepared.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            We'll program it with your unique card URL and ship it to you.
          </p>
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors" data-testid="store-back-link">
            <ArrowLeft className="w-4 h-4" /> Set Up Your Card
          </Link>
        </div>
      </div>
    );
  }

  if (!product) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" /></div>;

  const cm = COLOR_MAP[color];

  return (
    <div className="min-h-screen bg-white" data-testid="store-page">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 lg:px-16 py-5 border-b border-gray-100">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="store-logo">
          <PlexataIcon size={28} />
          <span className="font-heading text-xl font-medium tracking-tight text-gray-900">plexata</span>
        </Link>
        <div className="flex items-center gap-5">
          {user ? (
            <Link to="/dashboard" className="text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors" data-testid="store-dashboard-link">Dashboard</Link>
          ) : (
            <Link to="/login" className="text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors">Already have an account? Log in</Link>
          )}
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Product Visual */}
          <div className="flex flex-col items-center lg:sticky lg:top-8">
            <div className="w-64 h-64 lg:w-80 lg:h-80 flex items-center justify-center transition-all duration-500">
              <img 
                src={cm.image} 
                alt={`${color} Plexata Ring`}
                className="w-full h-full object-contain drop-shadow-2xl transition-opacity duration-300"
                data-testid="product-image"
              />
            </div>
            <div className="flex gap-3 mt-6">
              {product.colors.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-10 h-10 rounded-full ${COLOR_MAP[c].bg} transition-all ${color === c ? `ring-2 ${COLOR_MAP[c].ring} ring-offset-2 scale-110` : 'opacity-60 hover:opacity-80'}`}
                  data-testid={`color-${c.toLowerCase()}`} />
              ))}
            </div>
            <p className={`mt-3 text-sm font-semibold ${cm.label}`}>{color}</p>
          </div>

          {/* Product Details & Checkout */}
          <div>
            <p className="text-sm font-semibold tracking-wider uppercase text-indigo-600 mb-2">Get Started</p>
            <h1 className="font-heading text-3xl lg:text-4xl font-normal tracking-tight text-gray-900 mb-3" data-testid="product-name">
              {product.name}
            </h1>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">{product.description}</p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6" data-testid="product-price">
              <span className="font-heading text-3xl font-normal text-gray-900">${finalPrice}</span>
              {promoApplied?.valid && <span className="text-lg text-gray-400 line-through">${product.price}</span>}
              <span className="text-sm text-gray-400">USD</span>
            </div>

            {/* Promo Banner */}
            {promo && !promoApplied?.valid && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="promo-banner">
                <Tag className="w-5 h-5 text-indigo-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-indigo-700">Use code {promo.code} for ${promo.discount} off</p>
                  <p className="text-xs text-indigo-500">Limited time offer</p>
                </div>
              </div>
            )}

            {/* Features */}
            <ul className="space-y-2 mb-6">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-indigo-600 shrink-0" /> {f}
                </li>
              ))}
            </ul>

            {/* Size */}
            <div className="mb-5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Ring Size (US)</label>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map(s => (
                  <button key={s} onClick={() => setSize(s)}
                    className={`w-11 h-11 rounded-xl font-semibold text-sm transition-all ${size === s ? 'bg-gray-900 text-white shadow-lg shadow-gray-900/20' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                    data-testid={`size-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Promo Code */}
            <div className="mb-6">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Promo Code</label>
              <div className="flex gap-2">
                <input value={promoCode} onChange={e => setPromoCode(e.target.value)} placeholder="Enter code"
                  className="flex-1 bg-white border border-gray-200 rounded-lg text-gray-900 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                  data-testid="promo-input" />
                <button onClick={applyPromo}
                  className="bg-gray-100 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors"
                  data-testid="apply-promo-btn">Apply</button>
              </div>
              {promoApplied && (
                <p className={`text-xs mt-2 font-medium ${promoApplied.valid ? 'text-emerald-600' : 'text-red-500'}`} data-testid="promo-result">
                  {promoApplied.valid ? `${promoApplied.description} — Applied!` : 'Invalid promo code'}
                </p>
              )}
            </div>

            {/* Account Section */}
            {user ? (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="logged-in-user">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{user.profile?.name || user.email}</p>
                  <p className="text-xs text-gray-500">Your ring will be linked to plexata.com/u/{user.username}</p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-5 mb-6" data-testid="create-account-section">
                <p className="text-sm font-semibold text-gray-900 mb-1">Create Your Account</p>
                <p className="text-xs text-gray-500 mb-4">Set up your profile to receive your personalized Plexata card</p>
                
                {/* Google OAuth Option */}
                <button
                  onClick={() => initiateGoogleAuth('store')}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-lg py-3 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all mb-4"
                  data-testid="google-signup-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs text-gray-400 font-medium">or fill in manually</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Full Name</label>
                    <input 
                      type="text"
                      value={accountForm.name}
                      onChange={e => setAccountForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Doe"
                      className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                      data-testid="account-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
                    <input 
                      type="email"
                      value={accountForm.email}
                      onChange={e => setAccountForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                      data-testid="account-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Username</label>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-400 bg-gray-100 px-3 py-2.5 rounded-l-lg border border-r-0 border-gray-200">plexata.com/u/</span>
                      <input 
                        type="text"
                        value={accountForm.username}
                        onChange={e => setAccountForm(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                        placeholder="johndoe"
                        className="flex-1 bg-white border border-gray-200 rounded-r-lg text-gray-900 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                        data-testid="account-username-input"
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">This will be your public card URL programmed into your ring</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Password</label>
                    <div className="relative">
                      <input 
                        type={showPassword ? "text" : "password"}
                        value={accountForm.password}
                        onChange={e => setAccountForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Min. 6 characters"
                        className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                        data-testid="account-password-input"
                      />
                      <button 
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-lg mb-4" data-testid="checkout-error">
                {error}
              </div>
            )}

            {/* Checkout */}
            <button onClick={checkout} disabled={loading}
              className="w-full bg-gray-900 text-white py-4 rounded-xl font-semibold text-sm hover:bg-black transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-gray-900/10"
              data-testid="checkout-btn">
              <ShoppingBag className="w-4 h-4" />
              {loading ? 'Processing...' : `Order Now — $${finalPrice}`}
            </button>
            <p className="text-[10px] text-gray-400 text-center mt-3">Secure checkout powered by Stripe. Shipping address collected at checkout.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Store;
